/**
 * Guest data migration — reassigns all guest-owned records to the authenticated user.
 *
 * Called on every successful authentication. Uses a single Dexie transaction to
 * atomically update `createdBy` from the all-zeros guest UUID to the Cognito sub.
 * If no guest records exist, the transaction is a no-op.
 */

import { db } from '../../database/schema.js'
import { GUEST_USER_ID } from './auth-context.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('data-migration')

/** Tables that have a `createdBy` field indexed for querying. */
const TABLES_WITH_CREATED_BY = [
  'routes',
  'plans',
  'activities',
  'features',
  'trips',
  'tripFeatures',
  'layerGroups',
  'teams',
  'packLists',
] as const

/**
 * Migrate all guest-owned records to the given authenticated user ID.
 * Returns the total number of records updated.
 */
export async function migrateGuestData(userId: string): Promise<number> {
  if (userId === GUEST_USER_ID) return 0

  let totalMigrated = 0

  await db.transaction(
    'rw',
    TABLES_WITH_CREATED_BY.map(t => db.table(t)),
    async () => {
      for (const tableName of TABLES_WITH_CREATED_BY) {
        const table = db.table(tableName)
        const count = await table
          .where('createdBy')
          .equals(GUEST_USER_ID)
          .modify({ createdBy: userId })
        totalMigrated += count
      }
    }
  )

  if (totalMigrated > 0) {
    log.info(`Migrated ${totalMigrated} guest records to user ${userId}`)
  }

  return totalMigrated
}
